import './UsersListBody.scss';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'src/modules/common/components/Button';
import { Modal } from 'src/modules/common/components/Modal';
import { DeleteIcon } from 'src/modules/common/icons/DeleteIcon';
import { AuthUser } from 'src/modules/common/types/AuthUser';
import { UserListActions } from 'src/modules/users/components/UserListActions';
import { UserListHeader } from 'src/modules/users/components/UserListHeader';
import { UserRow } from 'src/modules/users/components/UserRow';
import { OrganizationUser } from 'src/modules/users/types/OrganizationUser';
import { SortingOrder, SortingTarget } from 'src/modules/users/types/Sorting';
import { UserDeleting } from 'src/modules/users/types/UserDeleting';
import { DeleteUserModal } from 'src/modules/users/views/DeleteUserModal';

type Props = {
  readonly searchQuery: string;
  readonly setSearchQuery: (query: string) => void;
  readonly sortingTarget: SortingTarget;
  readonly setSortingTarget: (target: SortingTarget) => void;
  readonly sortingOrder: SortingOrder;
  readonly setSortingOrder: (order: SortingOrder) => void;
  readonly sortedFoundUsers: ReadonlyArray<OrganizationUser>;
  readonly sortedFoundSelectableUsers: ReadonlyArray<OrganizationUser>;
  readonly loggedInUser: AuthUser;
  readonly userDeleting: UserDeleting | null;
  readonly handleDelete: (data: ReadonlyArray<OrganizationUser>) => void;
  readonly handleAddUserOpen: () => void;
};

export const UsersListBody = memo(({
  searchQuery,
  setSearchQuery,
  sortingTarget,
  setSortingTarget,
  sortingOrder,
  setSortingOrder,
  sortedFoundUsers,
  sortedFoundSelectableUsers,
  loggedInUser,
  userDeleting,
  handleDelete,
  handleAddUserOpen,
}: Props): React.ReactElement => {
  const [isDeleteUserModalOpen, setDeleteUserModalOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<ReadonlyArray<OrganizationUser>>([]);

  const areSomeSelected = selectedUsers.length !== 0 &&
    selectedUsers.length !== sortedFoundSelectableUsers.length;
  const areAllSelected = selectedUsers.length !== 0 &&
    selectedUsers.length === sortedFoundSelectableUsers.length;

  const handleSelectAll = (): void => {
    setSelectedUsers(sortedFoundSelectableUsers);
  };

  const handleSelectNone = (): void => {
    setSelectedUsers([]);
  };

  const handleHeaderCheckboxChange = (): void => {
    if (areAllSelected) {
      return setSelectedUsers([]);
    }

    return setSelectedUsers(sortedFoundSelectableUsers);
  };

  const handleDeleteUserCancel = (): void => {
    setDeleteUserModalOpen(false);
  };

  const handleDeleteUserSubmit = (): void => handleDelete(selectedUsers);

  const handleSelect = useCallback((selectedUser: OrganizationUser): void => {
    const selectedUserIds = selectedUsers.map((user) => user.id);
    const checked = selectedUserIds.includes(selectedUser.id);

    if (!checked) {
      return setSelectedUsers((prev) => [...prev, selectedUser]);
    }

    const filteredUsers = selectedUsers.filter((user) => user.id !== selectedUser.id);

    return setSelectedUsers(filteredUsers);
  }, [selectedUsers]);

  useEffect(() => {
    const allSelectableUserIds = sortedFoundSelectableUsers.map((user) => user.id);
    setSelectedUsers((prev) => prev.filter((user) => allSelectableUserIds.includes(user.id)));
  }, [sortedFoundSelectableUsers]);

  useEffect(() => {
    if (userDeleting?.status.state === 'success') {
      setDeleteUserModalOpen(false);
    }
  }, [userDeleting?.status]);

  return (
    <div className="bp-users-list-body">
      <div className="bp-users-list-body__content">

        {selectedUsers.length !== 0 ? (
          <div className="bp-users-list-body__selections">
            <div className="bp-users-list-body__selection-group">
              <FormattedMessage id="admin/users/list/selected" values={{ count: selectedUsers.length }}/>

              <div className="bp-users-list-body__selection-divider"/>

              <div className="bp-users-list-body__selection-button">
                <Button size="md" onClick={handleSelectAll}>
                  <FormattedMessage
                    id="admin/users/list/selectAll"
                    values={{ count: sortedFoundSelectableUsers.length }}
                  />
                </Button>
              </div>
            </div>

            <div className="bp-users-list-body__selection-group">
              <Button size="md" onClick={() => setDeleteUserModalOpen(true)}>
                <FormattedMessage id="admin/users/list/delete"/>

                <div className="bp-users-list-body__icon">
                  <DeleteIcon/>
                </div>
              </Button>

              <div className="bp-users-list-body__selection-divider"/>

              <Button size="md" onClick={handleSelectNone}>
                <FormattedMessage id="admin/users/list/cancel"/>
              </Button>
            </div>
          </div>
        ) : (
          <UserListActions
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            handleAddUserOpen={handleAddUserOpen}
          />
        )}

        <UserListHeader
          areAllSelected={areAllSelected}
          areSomeSelected={areSomeSelected}
          sortingOrder={sortingOrder}
          sortingTarget={sortingTarget}
          setSortingOrder={setSortingOrder}
          setSortingTarget={setSortingTarget}
          handleHeaderCheckboxChange={handleHeaderCheckboxChange}
        />

        {sortedFoundUsers.map((user) => (
          <UserRow
            key={user.id}
            isSelf={loggedInUser.id === user.id}
            currentUser={user}
            isSelected={selectedUsers.map((selectedUser) => selectedUser.id).includes(user.id)}
            onSelectHandler={handleSelect}
          />
        ))}

        <Modal
          handleClose={handleDeleteUserCancel}
          opened={isDeleteUserModalOpen}
          size="sm"
        >
          <DeleteUserModal
            removing={userDeleting !== null && userDeleting.status.state === 'pending'}
            selectedUsers={selectedUsers}
            onDelete={handleDeleteUserSubmit}
            onClose={handleDeleteUserCancel}
          />
        </Modal>
      </div>
    </div>
  );
});
